import {
  aliasDriverSpace, answerChoicesFor, answerForDrivenRow, collectAliasUsages, familyNameOf,
  lastAnswersFor, resolveAlias,
  type AnswerRefAlias, type RefCensusTask, type SessionRecord,
} from '@agavi/blocks';

import type { AnswerRefContextValue } from '@/components/task/answer-ref-context';

/** One reference this task is waiting on, and the answers that could stand in for it. */
export interface RecallFeeder {
  /**
   * The references this row speaks for -- usually one, several when they are facets of the same
   * answer offering the same choices. `mergeFacetRows` has the rule.
   */
  aliasIds: string[];
  /** What the chip calls itself in the prose, so the row and the sentence agree. */
  name: string;
  choices: { label: string; sample: unknown }[];
  /**
   * The blocks ON THIS CANVAS that cite the alias — what a highlight has to land on.
   *
   * Exit-slot citations are deliberately left out. `collectAliasUsages` reports them with the
   * exit block's own id, which is real but is not a member of `task.blocks`, so a highlight
   * keyed on it would mark nothing and a census counting it would overstate the reach.
   */
  blockIds: string[];
}

/**
 * THE REFERENCES THIS TASK CANNOT ANSWER YET.
 *
 * Shared by the Inspect panel, which lists them and offers a stand-in for each, and the task
 * surface, which rings the blocks that are waiting. Both have to agree about which references
 * are outstanding: a band naming one block and a ring drawn around another is worse than
 * neither, so there is one computation and they read it the same way.
 *
 * MEMBERSHIP IS "NOTHING WALKED HERE", not "nothing is set". That is the same test the renderer
 * makes, and it is what keeps a row in place after the teacher fills it: a stand-in is consulted
 * only where no real answer exists, so setting one does not change the answer to this question.
 * The row stays, holding the value, ready to be changed -- which is the point. It leaves only
 * when the run actually reaches the source, at which point the student's own answer has won and
 * a control over a stand-in nobody reads would be a lie.
 *
 * Callers that want "still blank" rather than "still waiting" -- the ring is one -- ask the
 * stand-in map, which is why that map is not consulted here.
 *
 * `collectAliasUsages` rather than a walk of this task's blocks: it already covers prose,
 * formula tokens AND exit-text blocks, and the last of those is a citation a hand-rolled scan
 * has forgotten before -- see its own note.
 */
export function recallFeeders(
  scope: AnswerRefContextValue | null,
  session: SessionRecord | null,
  taskId: string,
  name: (alias: AnswerRefAlias) => string,
): RecallFeeder[] {
  if (!scope || !session) return [];
  const sites = new Map<string, string[]>();
  for (const u of collectAliasUsages(scope.tasks as RefCensusTask[])) {
    if (u.inTaskId !== taskId) continue;
    const at = sites.get(u.aliasId);
    if (at) { if (!u.inExitSlot && !at.includes(u.inBlockId)) at.push(u.inBlockId); }
    else sites.set(u.aliasId, u.inExitSlot ? [] : [u.inBlockId]);
  }
  const rows: RecallFeeder[] = [];
  for (const [aliasId, blockIds] of sites) {
    const alias = scope.aliases.find(a => a.id === aliasId);
    if (!alias) continue;
    /* The renderer's own test for "a real answer reached this", so the band and the sentence
       cannot disagree about which references are still waiting. */
    const walked = resolveAlias(alias, taskId, scope.tasks as RefCensusTask[],
      m => lastAnswersFor(session, m.taskId, 'session')[m.blockId] !== undefined);
    if (walked) continue;
    const live = alias.members
      .map(m => {
        const task = scope.tasks.find(tk => tk.id === m.taskId);
        const block = task?.blocks?.find(b => b.id === m.blockId);
        return block ? { m, block } : null;
      })
      .filter((x): x is { m: typeof alias.members[number]; block: NonNullable<typeof x>['block'] } => !!x);
    if (live.length === 0) continue;

    /**
     * THE UNION'S ROWS, NOT ONE MEMBER'S OPTIONS.
     *
     * This took the first live member and offered ITS answers, which is how the owner's
     * three-pathway alias ended up offering two: the first member asks about two critters and
     * the other two members ask about three more between them. Everything only a later member
     * could answer was unreachable, so the driven version pager stepped 1, 2 and wrapped.
     *
     * A multi-member alias means EQUIVALENTS, and its answer space is the union of what its
     * members accept — which `aliasDriverSpace` already computes, merging by text so one
     * authored row serves every member that offers the same word.
     *
     * The first-member rule was written because the pick handler addressed that member alone.
     * It no longer does: a stand-in is keyed by the ALIAS and offered to every member, because
     * a student is on exactly one pathway and the teacher is standing in for whichever it was.
     * See `useDrivenVersionInForce`, where that half was fixed and this half was not.
     */
    const space = alias.members.length > 1
      ? aliasDriverSpace(alias, scope.tasks as Parameters<typeof aliasDriverSpace>[1],
        scope.experienceVariableNames ?? new Set<string>())
      : null;

    const choices = space?.rows
      ? space.rows.flatMap(row => {
        /*
         * ANY member's type and facet will do, which is not an accident: `aliasFormationFaults`
         * refuses a `typeMismatch` and a `facetMismatch`, so every member of a well-formed
         * alias answers the same KIND of question the same way. Looking up the member a row
         * came from and asking it instead is the same call with more steps — measured, by
         * mutating the lookup away and watching nothing fail.
         *
         * What makes that safe is the resolver, not this: `aliasDrivenRowId` now prefers a
         * member that RECOGNISES the answer over one that merely catches it, so a sample lands
         * on its own row whichever member it was shaped by.
         */
        const sample = answerForDrivenRow(live[0]!.block.type, row, live[0]!.m.facet);
        return sample === null || sample === undefined
          ? [] : [{ id: row.id, label: row.label, kind: undefined, sample }];
      })
      : answerChoicesFor(
        live[0]!.block.type, (live[0]!.block.data ?? {}) as Record<string, unknown>,
        { variables: scope.benchVariablesFor?.(live[0]!.m.taskId) ?? {}, facet: live[0]!.m.facet },
      ).filter(c => c.sample !== null);
    rows.push({
      aliasIds: [aliasId],
      name: name(alias),
      choices: choices.map((c, i) => ({ label: c.label ?? `${i + 1}`, sample: c.sample })),
      blockIds,
    });
  }
  return mergeFacetRows(rows, scope);
}

/**
 * ONE ROW PER QUESTION, not per facet.
 *
 * A numerical quoted two ways -- "thing (full)" and "thing (number)" -- is two aliases and two
 * rows, and they offer the SAME three answers, because the choices come from the block's own
 * answer and the facet only decides how much of it is printed. A teacher setting one then the
 * other is answering the same question twice, in a band whose whole budget in the landscape
 * dock is a few rows tall.
 *
 * MERGED ONLY WHERE THE CHOICES ARE IDENTICAL. A facet that offers something different is a
 * different question and keeps its row; `unit` and `uncertainty` offer nothing enumerable at
 * all, so they never merge with the value facets and never silently take their answer.
 *
 * AND ONLY WHERE THE FAMILY HAS A NAME, because the merged row has to be labelled with
 * something true. The name belongs to the block, so it covers every facet of it -- that is what
 * makes it the honest label for a row that sets them all. An unnamed family's rows read as
 * addresses ending in their own facet, and one of those cannot stand for the other.
 */
function mergeFacetRows(rows: RecallFeeder[], scope: AnswerRefContextValue): RecallFeeder[] {
  const out: RecallFeeder[] = [];
  const at = new Map<string, RecallFeeder>();
  for (const row of rows) {
    const alias = scope.aliases.find(a => a.id === row.aliasIds[0]);
    const first = alias?.members[0];
    const family = first ? familyNameOf(first, scope.aliases) : null;
    /* Keyed on the blocks quoted and the answers offered: same source, same choices, one row.
       An EMPTY choice list never merges: what justifies folding two rows together is the answers
       they share, and two facets that enumerate nothing share nothing -- they are merely both
       unanswerable, which is not evidence they are the same question. */
    const key = family && alias && row.choices.length > 0
      ? JSON.stringify([
        family,
        alias.members.map(m => `${m.taskId}\u0000${m.blockId}`).sort(),
        row.choices.map(c => c.sample),
      ])
      : null;
    const held = key ? at.get(key) : undefined;
    if (held) {
      held.aliasIds.push(...row.aliasIds);
      for (const id of row.blockIds) if (!held.blockIds.includes(id)) held.blockIds.push(id);
      /* The family name, with no facet suffix: the row now speaks for all of them. */
      held.name = family!;
      continue;
    }
    if (key) at.set(key, row);
    out.push(row);
  }
  return out;
}

/**
 * WHAT MARK A BLOCK WEARS, given everything the panel knows.
 *
 * WAITING IS "STILL BLANK", which is narrower than a feeder's own membership: a filled row stays
 * in the band on purpose, holding its value so the teacher can change it, and a ring around the
 * block it feeds would then be marking a question that has an answer.
 *
 * LIT WINS. The teacher is hovering that reference's row and asking what it feeds; a pointer
 * that vanished on the very blocks it was pointing at -- the filled ones -- would be answering
 * the opposite question. So a lit alias marks its blocks whether or not it is set, and a block
 * fed by two references takes the lit one.
 */
export function recallMarkFor(
  feeders: readonly RecallFeeder[],
  standIns: Record<string, unknown>,
  lit: string | null,
  blockId: string,
): 'waiting' | 'lit' | null {
  let mark: 'waiting' | null = null;
  for (const f of feeders) {
    if (!f.blockIds.includes(blockId)) continue;
    if (f.aliasIds.includes(lit ?? '')) return 'lit';
    /* ANY of a merged row's references being blank leaves the block waiting: the row sets them
       together, but the chips in the prose can still set one alone. */
    if (f.aliasIds.some(id => standIns[id] === undefined)) mark = 'waiting';
  }
  return mark;
}
